// src/components/BraunClock.jsx
import React, { useEffect, useState } from "react";

const formatSystemTime = (date) => {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const isAM = hours < 12;
  const displayHours = hours % 12 === 0 ? 12 : hours % 12;

  return {
    currentSystemTime: `${displayHours}:${minutes.toString().padStart(2, "0")}`,
    ampm: isAM ? "AM" : "PM",
  };
};

/**
 * Formats a countdown in seconds into DD:HH:MM:SS, HH:MM:SS, or MM:SS format
 * based on the magnitude of the countdown.
 * @param {number|null} seconds - The total seconds remaining for the countdown.
 * @returns {string} - Formatted countdown string.
 */
const formatCountdown = (seconds) => {
  if (seconds === null || seconds < 0) {
    return "00:00"; // Default to MM:SS if no valid countdown
  }

  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const pad = (num) => String(num).padStart(2, "0");

  if (days > 0) {
    return `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(
      remainingSeconds
    )}`;
  } else if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(remainingSeconds)}`;
  } else {
    return `${pad(minutes)}:${pad(remainingSeconds)}`;
  }
};

const BraunClock = ({ countdown = null, gameStatus = "active" }) => {
  const [time, setTime] = useState(new Date());
  const [isAlarmActive, setIsAlarmActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const { currentSystemTime, ampm } = formatSystemTime(time);

  useEffect(() => {
    // Alarm activates when game is pending and countdown is 10 seconds or less
    if (gameStatus === "pending" && countdown !== null && countdown <= 10) {
      setIsAlarmActive(true);
    } else {
      setIsAlarmActive(false);
    }
  }, [countdown, gameStatus]);

  // Determine the content for the main display based on the countdown value
  const mainDisplayContent =
    countdown !== null ? formatCountdown(countdown) : "00:00";

  // Calculate dynamic sizes based on the length of the main display content
  // This allows the clock to adjust its size based on "DD:HH:MM:SS", "HH:MM:SS", or "MM:SS"
  const contentLen = mainDisplayContent.length;
  let clockSize = 120; // Default size for MM:SS
  let innerDisplaySize = 80;
  let mainFontSize = 25;
  let radius = 30;
  let dotTranslate = 45; // For the decorative dots
  let borderThickness = 12; // Border thickness for the outer ring
  let innerBorderThickness = 2; // Border thickness for the inner display
  let letterSpacing = "2px"; // Default letter spacing for main display

  if (contentLen > 5 && contentLen <= 8) {
    // HH:MM:SS (length 8)
    clockSize = 150;
    innerDisplaySize = 100;
    mainFontSize = 30;
    radius = 40;
    dotTranslate = 58;
    borderThickness = 15;
    innerBorderThickness = 3;
    letterSpacing = "1.5px"; // Slightly reduce letter spacing
  } else if (contentLen > 8) {
    // DD:HH:MM:SS (length 11)
    clockSize = 180;
    innerDisplaySize = 120;
    mainFontSize = 23; // Decreased font size for long digits
    radius = 50;
    dotTranslate = 70;
    borderThickness = 18;
    innerBorderThickness = 4;
    letterSpacing = "1px"; // Further reduce letter spacing for longest digits
  }

  /**
   * Renders text along a curve, useful for clock labels.
   * @param {string} text - The text to render.
   * @param {number} radius - The radius of the curve.
   * @param {number} startAngle - The starting angle in degrees for the first letter.
   * @param {string} className - Tailwind CSS classes for styling.
   * @returns {Array<JSX.Element>} - Array of span elements for each letter.
   */
  const renderCurvedText = (text, radius, startAngle, className) => {
    const letters = text.replace(/ /g, "\u00a0").split(""); // Replace spaces with non-breaking spaces
    let angleIncrement = 0;
    let adjustedStartAngle = startAngle;

    // Special handling for "SNOOZE/LIGHT" and "SCITECH" to center them
    if (text === "SNOOZE/LIGHT") {
      const numLetters = text.length;
      angleIncrement = 120 / (numLetters - 1); // Spread over 120 degrees
      const centerAngle = 90; // Center at 90 degrees (top)
      adjustedStartAngle =
        centerAngle - ((numLetters - 1) * angleIncrement) / 2;
    } else if (text === "SCITECH") {
      const numLetters = text.length;
      angleIncrement = 120 / (numLetters - 1); // Spread over 120 degrees
      const centerAngle = 270; // Center at 270 degrees (bottom)
      adjustedStartAngle =
        centerAngle - ((numLetters - 1) * angleIncrement) / 2;
    }

    return letters.map((letter, i) => {
      const currentAngle = adjustedStartAngle + i * angleIncrement;
      // Calculate position on the circle
      const x = 50 + radius * Math.cos((currentAngle * Math.PI) / 180);
      const y = 50 + radius * Math.sin((currentAngle * Math.PI) / 180);
      return (
        <span
          key={i}
          className={`absolute ${className}`}
          style={{
            left: `${x}%`,
            top: `${y}%`,
            // Rotate each letter to follow the curve
            transform: `translate(-50%, -50%) rotate(${currentAngle + 90}deg)`,
            transformOrigin: "center center",
            filter: isHovered ? "brightness(1.2)" : "none",
            transition: "filter 0.3s ease",
          }}
        >
          {letter}
        </span>
      );
    });
  };

  return (
    <div
      className="mx-auto flex flex-col items-center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="relative transition-all duration-300"
        style={{
          width: `${clockSize}px`, // Dynamic overall clock size
          height: `${clockSize}px`, // Dynamic overall clock size
          borderRadius: "50%",
          background: `
            radial-gradient(
              circle at 30% 30%,
              #5a5a5a 0%,
              #3a3a3a 30%,
              #2a2a2a 70%,
              #1a1a1a 100%
            )`,
          boxShadow: `
            0 10px 20px rgba(0,0,0,0.9),
            ${isHovered ? "0 0 20px rgba(100,200,255,0.3)," : ""}
            inset 0 4px 12px rgba(255,255,255,0.08),
            inset 0 -8px 20px rgba(0,0,0,0.8)
          `,
          transform: `perspective(800px) rotateX(${
            isHovered ? "12deg" : "10deg"
          })`,
          border: "1px solid #444",
          transition: "all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
        }}
      >
        {/* Inner bezel and grid */}
        <div
          className="absolute inset-0 rounded-full overflow-hidden"
          style={{
            border: `${borderThickness}px solid #333`, // Dynamic border thickness
            background: `
              linear-gradient(
                135deg,
                rgba(180,180,180,0.15) 0%,
                rgba(230,230,230,0.08) 50%,
                rgba(180,180,180,0.15) 100%
              )`,
            boxShadow: `
              inset 0 3px 8px rgba(255,255,255,0.15),
              inset 0 -3px 8px rgba(0,0,0,0.6),
              0 1px 2px rgba(0,0,0,0.4)
            `,
          }}
        >
          {/* Subtle grid pattern */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
              radial-gradient(circle at 50% 50%,
                rgba(255,255,255,0.8) 1px,
                transparent 1px
              )`,
              backgroundSize: "8px 8px",
            }}
          />

          {/* Curved text labels */}
          <div className="absolute inset-0">
            {renderCurvedText(
              "SNOOZE/LIGHT",
              radius, // Dynamic radius for text placement
              0, // Start angle (will be adjusted internally for centering)
              "text-[#c0c0c0] text-[8px] tracking-[3px] font-light"
            )}
            {renderCurvedText(
              "SCITECH",
              radius, // Dynamic radius for text placement
              0, // Start angle (will be adjusted internally for centering)
              "text-[#f0f0f0] text-[10px] font-bold tracking-[2px]"
            )}
          </div>
        </div>

        {/* Main digital display area */}
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full flex flex-col items-center justify-center transition-all duration-500"
          style={{
            width: `${innerDisplaySize}px`, // Dynamic inner display size
            height: `${innerDisplaySize}px`, // Dynamic inner display size
            background: `
              linear-gradient(
                160deg,
                #003a4a 0%,
                #002a35 50%,
                #001a25 100%
              )`,
            border: `${innerBorderThickness}px solid #111`, // Dynamic inner border thickness
            boxShadow: `
              inset 0 4px 10px rgba(100,200,255,0.1),
              inset 0 -5px 12px rgba(0,0,0,0.9),
              0 3px 6px rgba(0,0,0,0.5)
            `,
            transform: `translate(-50%, -50%) scale(${isHovered ? 1.02 : 1})`,
          }}
        >
          {/* Inner grid pattern for the display */}
          <div
            className="absolute inset-0 rounded-full opacity-10"
            style={{
              backgroundImage: `
              linear-gradient(rgba(100,200,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(100,200,255,0.1) 1px, transparent 1px)`,
              backgroundSize: "2px 2px",
            }}
          />

          {/* Countdown timer display */}
          <div
            className="font-bold transition-all duration-300"
            style={{
              fontFamily: "'Digital-7', monospace",
              color:
                gameStatus === "pending" &&
                countdown !== null &&
                countdown <= 10
                  ? "#ff6060"
                  : "#e0e0e0",
              lineHeight: "1",
              letterSpacing: letterSpacing, // Dynamic letter spacing
              filter: isHovered ? "brightness(1.1)" : "none",
              marginBottom: "3px",
              fontSize: `${mainFontSize}px`, // Dynamic font size
            }}
          >
            {mainDisplayContent}
          </div>

          {/* System time and AM/PM display */}
          <div className="flex items-baseline gap-x-0.5 bg-black bg-opacity-40 px-1.5 py-0.5 rounded-full backdrop-blur-sm border border-[#222]">
            <>
              <style>
                {`
                  @keyframes ring {
                    0% { transform: rotate(0deg); }
                    15% { transform: rotate(15deg); }
                    30% { transform: rotate(-12deg); }
                    45% { transform: rotate(8deg); }
                    60% { transform: rotate(-6deg); }
                    75% { transform: rotate(3deg); }
                    100% { transform: rotate(0deg); }
                  }

                  @keyframes glow {
                    0%, 100% { filter: drop-shadow(0 0 2px rgba(255,100,100,0.5)); }
                    50% { filter: drop-shadow(0 0 6px rgba(255,80,80,1)); }
                  }
                `}
              </style>

              <span
                className={`text-[15px] ${
                  isAlarmActive ? "text-[#ff7070]" : "text-[#b0b0b0]"
                }`}
                style={{
                  display: "inline-block",
                  textShadow: `0 0 4px ${
                    isAlarmActive
                      ? "rgba(255,100,100,0.7)"
                      : "rgba(180,180,180,0.5)"
                  }`,
                  animation: isAlarmActive
                    ? "ring 0.6s ease-in-out infinite, glow 1.2s ease-in-out infinite"
                    : "none",
                  transformOrigin: "50% 0%", // makes the swing realistic
                }}
              >
                🔔
              </span>
            </>

            <span
              className="text-[15px] transition-all duration-300"
              style={{
                fontFamily: "'Digital-7', monospace",
                color: "#e0e0e0",
                textShadow: `
                  0 0 5px rgba(220,220,220,0.5),
                  0 1px 2px rgba(0,0,0,0.8)
                `,
                letterSpacing: "0.5px",
                transform: isHovered ? "scale(1.05)" : "scale(1)",
              }}
            >
              {currentSystemTime}
            </span>
            <span
              className="text-[8px] ml-0.5"
              style={{
                color: "#d0d0d0",
                textShadow: "0 1px 2px rgba(0,0,0,0.8)",
                letterSpacing: "0.5px",
                transform: isHovered ? "scale(1.05)" : "scale(1)",
              }}
            >
              {ampm}
            </span>
          </div>
        </div>

        {/* Decorative circular elements */}
        {[45, 135, 225, 315].map((angle) => (
          <div
            key={angle}
            className="absolute w-[8px] h-[8px] rounded-full z-8 transition-all duration-300"
            style={{
              top: "50%",
              left: "50%",
              transform: `
                translate(-50%, -50%)
                rotate(${angle}deg)
                translate(${dotTranslate}px)
                rotate(-${angle}deg)
                rotate(${isHovered ? "180deg" : "0deg"})
              `,
              background: `
                radial-gradient(
                  circle at 30% 30%,
                  #808080 0%,
                  #606060 50%,
                  #404040 100%
                )`,
              border: "1px solid #000",
              boxShadow: `
                inset 0 1px 2px rgba(255,255,255,0.2),
                0 1px 3px rgba(0,0,0,0.8),
                ${isHovered ? "0 0 5px rgba(100,200,255,0.4)," : ""}
                0 0 0 1px rgba(60,60,60,0.8)
              `,
            }}
          >
            {/* Inner smaller circle */}
            <div
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[3px] h-[3px] rounded-full"
              style={{
                background: "#303030",
                boxShadow: "inset 0 1px 1px rgba(0,0,0,0.8)",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default BraunClock;
